import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo, useRef, useCallback, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Map, List } from 'immutable';
import classNames from 'classnames';
import Draggable from 'react-draggable';

import { ResizablePanel, PanelHeader, PanelBody } from '../../helpers/ResizablePanel';
import Checkbox from '../../helpers/Checkbox';
import { userSelector } from '../../../selectors/user';
import { loadWidgetData } from '../../../actions/dashboards';
import { isResearcher } from '../../../utils/checkPermissions';
import DashboardData from '../DashboardData';

import style from './style.module.css';

/**
 * For the Backlog widget get a sum of Quantity column.
 *
 * @param {string} name Widget name.
 * @param {Map} widget Widget data.
 * @returns {string} String with the sum of quantity.
 */
function getQuantitySum(name, widget) {
  if (name !== 'backlog' || widget.get('data').size === 0) return;

  const data = widget.get('data').toJS();
  const quantitySum = data.reduce((sum, { targetCount }) => sum + targetCount, 0);

  return `(${quantitySum})`;
}

/**
 * Dashboard Panel. Show "No rows to display" if `data` property is empty List.
 *
 * @param props {Object}.
 * @param props.title {String} Name of panel. Title is displayed at the top of the panel.
 * @param props.data {Immutable.List} List with some data to display. If empty - panels shows "No rows to display".
 * @param props.columns {Immutable.List} List of columns to display. Every column should has
 *    an `key` and `name` properties.
 */
const DashboardPanel = ({
  widget,
  name,
  serverSideSortingTables,
  onRowDoubleClick,
  onCellDoubleClick,
  onColumnResize,
  onClose,
  onToggle,
  onDragStart,
  onDragEnd,
  onResizeEnd,
  onGetNextPageData,
  onSort,
  zIndex,
  onClick,
  isDataFiltered,
  onSwitchFilterData,
  onChangeCheckboxId,
  onChangeAllPD,
  onConfirmDeletion,
  onConfirmReject,
  onUpdateSaveQuery,
  onRunQuery,
  onRunReport,
  onOpenReport,
  onDeleteSavedQuery,
  onDeleteMeeting,
  onViewMeetingDetailsNote,
}) => {
  const draggableRef = useRef(null);
  const dragStartPositionRef = useRef(null);
  const dispatch = useDispatch();
  const userData = useSelector(userSelector);

  /**
   * C1s widget user query selected.
   */
  const [tc1UserQuery, setTc1UserQuery] = useState(true);
  /**
   * C1s widget module query selected.
   */
  const [tc1ModuleQuery, setTc1ModuleQuery] = useState(false);
  const isCollapsed = widget.get('collapse', false);

  const handleToggle = useCallback(
    event => {
      event.stopPropagation();
      onToggle(event, name);
    },
    [onToggle, name],
  );

  const handleDragStart = useCallback(
    (event, { x, y }) => {
      dragStartPositionRef.current = { x, y };
      onDragStart(event, name);
    },
    [onDragStart, name],
  );

  const handleDragStop = useCallback(
    (event, { x, y }) => {
      const start = dragStartPositionRef.current;
      const isMoved = !start || start.x !== x || start.y !== y;

      onDragEnd(event, name, isMoved);
    },
    [onDragEnd, name],
  );

  /**
   * Has user access to dashboard panels computed value.
   */
  const hasAccess = useMemo(() => !isResearcher(userData.getIn(['roles', 0, 'slug'], null)), [userData]);

  /**
   * C1s widget query effect depends on module and user.
   */
  useEffect(() => {
    if (hasAccess) {
      const name = 'tc1';
      const user = userData.get('id');
      const module = userData.get('moduleId');

      if (tc1UserQuery && !tc1ModuleQuery) {
        dispatch(loadWidgetData({ name, query: { user } }));
      }

      if (tc1ModuleQuery && !tc1UserQuery) {
        dispatch(loadWidgetData({ name, query: { module } }));
      }

      if (tc1UserQuery && tc1ModuleQuery) {
        dispatch(loadWidgetData({ name, query: { user, module } }));
      }

      if (!tc1UserQuery && !tc1ModuleQuery) {
        dispatch(loadWidgetData({ name }));
      }
    }
  }, [tc1ModuleQuery, tc1UserQuery]);

  const visible = widget.get('visible');

  if (!visible) return null;

  const title = widget.get('title');
  const columns = widget.get('columns');
  const loading = widget.get('loading');
  const template = widget.get('urlTemplate');
  const meta = widget.get('meta', List());
  const data = widget.get('data', List());
  const widgetStyle = {
    position: 'absolute',
    ...widget.get('style').toJS(),
  };
  let titleActions = null;

  const handleC1sQueryChange = event => {
    const { name, checked } = event.target;

    if (name === 'user') setTc1UserQuery(checked);
    if (name === 'module') setTc1ModuleQuery(checked);
  };

  if (title === 'Research') {
    titleActions = (
      <a className="ml10" href="#" onClick={onSwitchFilterData}>
        {isDataFiltered ? 'Show All' : 'Show My'}
      </a>
    );
  }

  if (title === 'Backlog') {
    titleActions = (
      <div className={style.captions}>
        <span className={classNames(style.captionsSquare, style.captionsFuture)} />
        <span>- 10 days future Backlog</span>
        <span className={style.captionsSquare} />
        <span>- Current Backlog</span>
        <span className={classNames(style.captionsSquare, style.captionsPast)} />
        <span>- 10 days past Backlog</span>
      </div>
    );
  }

  /**
   * "C1s (T)" widget header customization.
   */
  if (title === 'C1s (T)') {
    titleActions = (
      <div className="d-flex">
        <div className="ml20 mr20">
          <Checkbox label="Module" name="module" onChange={handleC1sQueryChange} />
        </div>
        <Checkbox label="User" name="user" onChange={handleC1sQueryChange} defaultChecked />
      </div>
    );
  }

  /**
   * "Deletion Requests" widget header customization.
   */
  if (title === 'Deletion Requests') {
    const isDisabled = data.filter(item => item.get('selected')).size === 0;

    titleActions = (
      <div className="d-flex">
        <div className="ml20 mr20 d-flex">
          <Checkbox label="Select All" name="pd" onChange={onChangeAllPD} />
        </div>
        <button className="ml20 mr20" disabled={isDisabled} onClick={onConfirmDeletion}>
          Confirm
        </button>
        <button disabled={isDisabled} onClick={onConfirmReject}>
          Reject
        </button>
      </div>
    );
  }

  return (
    <Draggable
      axis="both"
      defaultPosition={{ x: widgetStyle.left, y: widgetStyle.top }}
      handle=".handle-drag"
      nodeRef={draggableRef}
      onStart={handleDragStart}
      onStop={handleDragStop}
      zIndex={zIndex}
    >
      <div ref={draggableRef} style={{ zIndex }}>
        <ResizablePanel
          collapsed={isCollapsed}
          height={widgetStyle.height}
          name={name}
          onClick={event => onClick(event, name)}
          onResizeEnd={(...args) => onResizeEnd(...args, name)}
          width={widgetStyle.width}
        >
          <PanelHeader
            className="handle-drag"
            collapsed={isCollapsed}
            onClick={event => onClick(event, name)}
            onClose={event => onClose(event, name)}
            onToggle={handleToggle}
          >
            <div className={style.header}>
              <b>
                {title} {getQuantitySum(name, widget)}
              </b>
              {titleActions}
            </div>
          </PanelHeader>
          <PanelBody>
            <DashboardData
              clickable={!!template}
              columns={columns}
              data={data}
              height={`calc(${widgetStyle.height}px - 34px)`}
              loading={loading}
              meta={meta}
              name={name}
              onCellDoubleClick={onCellDoubleClick}
              onChangeCheckboxId={onChangeCheckboxId}
              onColumnResize={onColumnResize}
              onDeleteMeeting={onDeleteMeeting}
              onDeleteSavedQuery={onDeleteSavedQuery}
              onGetNextPageData={onGetNextPageData}
              onOpenReport={onOpenReport}
              onRowDoubleClick={onRowDoubleClick}
              onRunQuery={onRunQuery}
              onRunReport={onRunReport}
              onSort={onSort}
              onUpdateSaveQuery={onUpdateSaveQuery}
              onViewMeetingDetailsNote={onViewMeetingDetailsNote}
              serverSideSortingTables={serverSideSortingTables}
              width={widgetStyle.width}
            />
          </PanelBody>
        </ResizablePanel>
      </div>
    </Draggable>
  );
};

DashboardPanel.propTypes = {
  widget: PropTypes.instanceOf(Map).isRequired,
};

export default memo(DashboardPanel);
